/**
 * useCameraCapabilities Hook - 2ECOMP Fleet Management
 *
 * Queries and monitors camera capabilities for a device.
 * Uses JT1078 0x9003 (queryCameraCapabilities) command to discover
 * available video channels on MettaX and similar MDVR devices.
 *
 * The response (0x1003) contains maxVideoChannels which indicates
 * how many camera channels the device supports (typically 1-8).
 */

import {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { useSelector } from 'react-redux';

/**
 * Default capabilities when unknown
 */
const DEFAULT_CAPABILITIES = {
  videoChannels: 4,
  maxVideoChannels: 4,
  maxAudioChannels: 0,
  audioChannels: 0,
  audioEncodingMethod: 0,
  videoEncodingMethod: 0,
  audioSampleRate: 0,
  audioSampleBits: 0,
  audioFrameLength: 0,
  supportAudioOutput: 0,
  source: 'default',
};

/**
 * Global cache for camera capabilities
 * Persists across component mounts/unmounts
 */
const capabilitiesCache = new Map();

/**
 * Hook for querying and monitoring camera capabilities
 *
 * @param {number|string} deviceId - Device ID to query
 * @param {Object} options - Configuration options
 * @returns {Object} Camera capabilities state and actions
 */
export const useCameraCapabilities = (deviceId, options = {}) => {
  const {
    autoQuery = true,
    cacheTimeout = 300000, // 5 minutes
    responseTimeout = 15000, // 15 seconds for response
  } = options;

  // Get device info from Redux
  const device = useSelector((state) => state.devices.items[deviceId]);
  const position = useSelector((state) => state.session.positions[deviceId]);

  // Check if device has manually configured channels
  const deviceChannels = device?.attributes?.cameraChannels;

  // Check cache
  const getCachedCapabilities = useCallback(() => {
    const cached = capabilitiesCache.get(String(deviceId));
    if (cached && Date.now() - cached.timestamp < cacheTimeout) {
      return cached.capabilities;
    }
    return null;
  }, [deviceId, cacheTimeout]);

  // Determine initial state
  const getInitialState = useCallback(() => {
    // Priority 1: Manual device attribute
    if (deviceChannels) {
      return {
        capabilities: {
          ...DEFAULT_CAPABILITIES,
          videoChannels: deviceChannels,
          maxVideoChannels: deviceChannels,
          source: 'manual',
        },
        isReady: true,
      };
    }

    // Priority 2: Cache
    const cached = getCachedCapabilities();
    if (cached) {
      return { capabilities: cached, isReady: true };
    }

    // Priority 3: Need to query
    return { capabilities: null, isReady: false };
  }, [deviceChannels, getCachedCapabilities]);

  const initial = getInitialState();
  const [capabilities, setCapabilities] = useState(initial.capabilities);
  const [isReady, setIsReady] = useState(initial.isReady);
  const [isQuerying, setIsQuerying] = useState(false);
  const [error, setError] = useState(null);

  const queryInProgressRef = useRef(false);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(true);

  /**
   * Parse capabilities from position attributes
   */
  const parseCapabilitiesFromPosition = useCallback((pos) => {
    if (!pos?.attributes?.result) return null;
    if (pos.attributes.result !== 'cameraCapabilities') return null;

    const attrs = pos.attributes;
    return {
      videoChannels: attrs.videoChannels || attrs.maxVideoChannels || DEFAULT_CAPABILITIES.videoChannels,
      maxVideoChannels: attrs.maxVideoChannels || attrs.videoChannels || DEFAULT_CAPABILITIES.maxVideoChannels,
      maxAudioChannels: attrs.maxAudioChannels || attrs.audioChannels || 0,
      audioChannels: attrs.audioChannels || attrs.maxAudioChannels || 0,
      audioEncodingMethod: attrs.audioEncodingMethod || 0,
      videoEncodingMethod: attrs.videoEncodingMethod || 0,
      audioSampleRate: attrs.audioSampleRate || 0,
      audioSampleBits: attrs.audioSampleBits || 0,
      audioFrameLength: attrs.audioFrameLength || 0,
      supportAudioOutput: attrs.supportAudioOutput || 0,
      source: 'device',
      receivedAt: new Date().toISOString(),
    };
  }, []);

  /**
   * Send queryCameraCapabilities command
   */
  const queryCapabilities = useCallback(async () => {
    if (!deviceId || queryInProgressRef.current) {
      return null;
    }

    queryInProgressRef.current = true;
    setIsQuerying(true);
    setError(null);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    try {
      const response = await fetch('/api/commands/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          deviceId: parseInt(deviceId, 10),
          type: 'queryCameraCapabilities',
        }),
      });

      if (!response.ok) {
        throw new Error(`Command failed: ${response.status}`);
      }

      const result = await response.json();

      // Set timeout for response
      timeoutRef.current = setTimeout(() => {
        if (mountedRef.current && queryInProgressRef.current) {
          queryInProgressRef.current = false;
          setIsQuerying(false);
          setIsReady(true);
          // Use defaults if timeout
          if (!capabilities) {
            setCapabilities(DEFAULT_CAPABILITIES);
          }
        }
      }, responseTimeout);

      return result;
    } catch (err) {
      console.error('[CameraCapabilities] Error:', err);
      if (mountedRef.current) {
        setError(err);
        setIsQuerying(false);
        setIsReady(true);
        queryInProgressRef.current = false;
        // Use defaults on error
        if (!capabilities) {
          setCapabilities(DEFAULT_CAPABILITIES);
        }
      }
      return null;
    }
  }, [deviceId, responseTimeout, capabilities]);

  /**
   * Monitor position updates for cameraCapabilities response
   */
  useEffect(() => {
    if (!position || !queryInProgressRef.current) return;

    const caps = parseCapabilitiesFromPosition(position);
    if (caps) {
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Update state
      setCapabilities(caps);
      setIsQuerying(false);
      setIsReady(true);
      queryInProgressRef.current = false;

      // Update cache
      capabilitiesCache.set(String(deviceId), {
        capabilities: caps,
        timestamp: Date.now(),
      });
    }
  }, [position, deviceId, parseCapabilitiesFromPosition]);

  /**
   * Auto-query on mount if needed
   */
  useEffect(() => {
    if (!autoQuery || !deviceId || isReady || queryInProgressRef.current) {
      return;
    }

    // If device has manual channels, use that
    if (deviceChannels) {
      const manualCaps = {
        ...DEFAULT_CAPABILITIES,
        videoChannels: deviceChannels,
        maxVideoChannels: deviceChannels,
        source: 'manual',
      };
      setCapabilities(manualCaps);
      setIsReady(true);
      return;
    }

    // Check cache again (in case it was populated by another component)
    const cached = getCachedCapabilities();
    if (cached) {
      setCapabilities(cached);
      setIsReady(true);
      return;
    }

    // Query device
    queryCapabilities();
  }, [deviceId, autoQuery, isReady, deviceChannels, getCachedCapabilities, queryCapabilities]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Get effective channel count
   */
  const channelCount = deviceChannels
    || capabilities?.videoChannels
    || DEFAULT_CAPABILITIES.videoChannels;

  /**
   * Clear cache and refresh
   */
  const refresh = useCallback(async () => {
    capabilitiesCache.delete(String(deviceId));
    setCapabilities(null);
    setIsReady(false);
    queryInProgressRef.current = false;
    return queryCapabilities();
  }, [deviceId, queryCapabilities]);

  return {
    // State
    capabilities: capabilities || DEFAULT_CAPABILITIES,
    isLoading: !isReady || isQuerying,
    isReady,
    isQuerying,
    error,
    hasCapabilities: !!capabilities && capabilities.source !== 'default',

    // Values
    channelCount,
    videoChannels: channelCount,
    hasAudio: (capabilities?.maxAudioChannels || 0) > 0,

    // Actions
    queryCapabilities,
    refresh,
  };
};

export default useCameraCapabilities;
